/**
 * markdownMathNormalizer.js
 *
 * Standalone, tunable normalizer for LLM Markdown output before it reaches
 * MarkdownRenderer (which has singleDollarTextMath: false to protect currency).
 *
 * Easy to toggle or tune via `MATH_NORMALIZER_CONFIG` below.
 */

export const MATH_NORMALIZER_CONFIG = {
  enabled: true,
  // Convert \(...\) and \[...\] LaTeX delimiters to $$...$$
  convertSlashDelimiters: true,
  // Selectively promote $...$ to $$...$$ when inner expression looks like LaTeX/math
  promoteSingleDollarMath: true,
  // Fix CommonMark-breaking space after opening bold fence: "** Why...**" -> "**Why...**"
  fixSpacedBoldOpeners: true,
};

// Matches fenced code blocks (```...```) or inline code spans (`...`) so they are untouched.
const CODE_SEGMENT_RE = /(```[\s\S]*?```|`[^`\n]+`)/g;

// Matches single-dollar pairs on a single line: $expr$ (not $$, not \$, no leading/trailing space,
// and closing $ not immediately followed by word/digit/dollar so "$10-$20" is never matched as "$10-$").
const SINGLE_DOLLAR_RE = /(?<![\\$\w])\$(?!\$)([^\s\n$](?:[^\n$]*?[^\s\n$])?)\$(?![\w$])/g;

// Guard A: reject multi-word currency phrases (e.g. "$100 in Q1 and $200", "$50,000 per month").
const CURRENCY_LIKE_RE = /^\d[\d,.]*(?:\s+(?:k|m|b|t|bn|mn|thousand|million|billion|trillion|usd|eur|gbp|cad|aud|jpy|chf|inr|cny|per|in|to|and|or|for|from|of|on|at|by|with|a|an|the)\b)/i;

// Guard B0: any single-token expression with no whitespace (e.g. "-1", "15", "s/r", "x", "CL_{iv}", "R^2")
// excluding dangling range prefixes like "10-" from "$10-$20".
const NO_SPACE_TOKEN_RE = /^[^\s]+$/;
const DANGLING_RANGE_RE = /^\d[\d,.]*[-–—]$/;

// Guard B1: explicit LaTeX markers (subscripts, superscripts, backslashes, braces).
const LATEX_MARKER_RE = /[_^\\{}]/;

// Guard B2: compact mathematical relation/equation (e.g. "p < 0.05", "n = 24", "x + y = z").
const MATH_EQUATION_RE = /^[A-Za-z0-9_^*+\-./()\s]+(?:=|<|>|<=|>=|!=|≈|±|×|÷)\s*[-+]?[A-Za-z0-9_^*+./()\s%]+$/;

// Guard B3: single math variable or simple function call (e.g. "x", "N", "f(x)", "g(x, y)").
const SINGLE_VARIABLE_RE = /^[A-Za-z](?:\([A-Za-z0-9,\s]+\))?$/;

/**
 * Returns true if the inner text of a single `$...$` span is unambiguously
 * mathematical/scientific notation and NOT currency.
 */
export function isPromotableInlineMath(expr) {
  if (!expr) return false;
  const trimmed = expr.trim();
  if (!trimmed || trimmed.length > 160) return false;

  // Reject dangling currency range prefixes (e.g. "10-" from "$10-$20")
  if (DANGLING_RANGE_RE.test(trimmed)) return false;

  // Any closed $...$ token with no internal whitespace is math (e.g. "$-1$", "$15$", "$s/r$", "$CL_{iv}$")
  if (NO_SPACE_TOKEN_RE.test(trimmed)) return true;

  // For multi-token expressions containing spaces, reject currency prose
  if (CURRENCY_LIKE_RE.test(trimmed) && !LATEX_MARKER_RE.test(trimmed)) {
    return false;
  }

  return (
    LATEX_MARKER_RE.test(trimmed) ||
    MATH_EQUATION_RE.test(trimmed) ||
    SINGLE_VARIABLE_RE.test(trimmed)
  );
}

function normalizeNonCodeSegment(segment, config) {
  let out = segment;

  if (config.convertSlashDelimiters) {
    // \[ ... \] -> block $$ ... $$
    out = out.replace(/\\\[([\s\S]+?)\\\]/g, (_, inner) => `\n$$\n${inner.trim()}\n$$\n`);
    // \( ... \) -> inline $$...$$
    out = out.replace(/\\\(([^\n]+?)\\\)/g, (_, inner) => `$$${inner.trim()}$$`);
  }

  if (config.promoteSingleDollarMath) {
    out = out.replace(SINGLE_DOLLAR_RE, (fullMatch, inner) => {
      if (isPromotableInlineMath(inner)) {
        return `$$${inner}$$`;
      }
      return fullMatch;
    });
  }

  if (config.fixSpacedBoldOpeners) {
    // Fix "** Why ...?**" -> "**Why ...?**" at line start or after whitespace/punctuation
    out = out.replace(/(^|[\s([{-])\*\*[ \t]+([^\s*\n][^\n*]*?)\*\*/gm, "$1**$2**");
  }

  return out;
}

/**
 * Normalizes math delimiters and spaced bold markers outside of code blocks.
 *
 * @param {string} text - Markdown string
 * @param {Partial<typeof MATH_NORMALIZER_CONFIG>} [overrides] - Optional config overrides
 * @returns {string}
 */
export function normalizeMarkdownMath(text, overrides) {
  if (!text || typeof text !== "string") return text;
  const config = overrides ? { ...MATH_NORMALIZER_CONFIG, ...overrides } : MATH_NORMALIZER_CONFIG;
  if (!config.enabled) return text;

  // Fast-path: skip regex splitting if none of the target characters exist
  if (!text.includes("$") && !text.includes("\\(") && !text.includes("\\[") && !text.includes("** ")) {
    return text;
  }

  const parts = text.split(CODE_SEGMENT_RE);
  for (let i = 0; i < parts.length; i += 2) {
    parts[i] = normalizeNonCodeSegment(parts[i], config);
  }
  return parts.join("");
}
